import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "video", "tab", "videoTabs", "playerContainer", "videoWrapper",
    "scoreOverlay", "scoreDisplay",
    "goalsList", "clipsList", "zoomIndicator"
  ]
  static values = {
    gameId: Number,
    apiBase: String,
    apiToken: String,
    gameData: Object,
    localVideos: Array
  }

  connect() {
    this.currentVideoIndex = 0
    this.marking = false
    this.markInTime = null
    this.zoomed = false
    this.panX = 0.5
    this.panY = 0.5
    this.dragging = false
    this.seekHeld = false

    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundKeyup = this.handleKeyup.bind(this)
    document.addEventListener("keydown", this.boundKeydown, true)
    document.addEventListener("keyup", this.boundKeyup, true)

    this.loadGameData()
    this.setupVideoListeners()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown, true)
    document.removeEventListener("keyup", this.boundKeyup, true)
  }

  get currentVideo() {
    return this.videoTargets[this.currentVideoIndex]
  }

  get currentProductionVideo() {
    if (!this.gameData) return null
    const videos = this.gameData.videos.filter(v => v.group_name === "Match")
    return videos[this.currentVideoIndex]
  }

  // ─── Data Loading ──────────────────────────────────

  loadGameData() {
    this.gameData = this.gameDataValue
    if (this.gameData) {
      this.renderGoals()
      this.renderClips()
      this.updateScoreDisplay()
    }
  }

  // ─── Video Selection ───────────────────────────────

  selectVideo(event) {
    const index = event.params.videoIndex
    if (index === this.currentVideoIndex) return

    this.currentVideo.pause()
    this.currentVideo.classList.add("hidden")

    this.currentVideoIndex = index
    this.currentVideo.classList.remove("hidden")

    // Update tab styles
    this.tabTargets.forEach((tab, i) => {
      tab.classList.toggle("bg-indigo-600", i === index)
      tab.classList.toggle("bg-gray-700", i !== index)
    })

    this.updateScoreDisplay()
    this.renderGoals()
  }

  setupVideoListeners() {
    this.videoTargets.forEach(video => {
      video.addEventListener("timeupdate", () => this.onTimeUpdate())
    })
  }

  onTimeUpdate() {
    this.updateScoreDisplay()
  }

  // ─── Keyboard Handling ─────────────────────────────

  handleKeydown(event) {
    if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") return

    switch (event.key.toLowerCase()) {
      case "m":
        event.preventDefault()
        this.toggleMark()
        break
      case "c":
        if (this.marking) { event.preventDefault(); this.cancelMark() }
        break
      case "f":
        event.preventDefault()
        this.toggleFullscreen()
        break
      case "p":
        event.preventDefault()
        event.stopImmediatePropagation()
        this.togglePlay()
        break
      case "h":
        event.preventDefault()
        event.stopImmediatePropagation()
        this.addGoal("home")
        break
      case "a":
        event.preventDefault()
        event.stopImmediatePropagation()
        this.addGoal("away")
        break
      case "z":
        event.preventDefault()
        this.toggleZoom()
        break
      case "s":
        event.preventDefault()
        this.saveCropPosition()
        break
      case "arrowleft":
      case "arrowright":
        event.preventDefault()
        event.stopImmediatePropagation()
        this.handleSeek(event)
        break
    }
  }

  handleKeyup(event) {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      this.stopFastSeek()
    }
  }

  // ─── Play/Pause ────────────────────────────────────

  togglePlay() {
    const video = this.currentVideo
    if (video) video.paused ? video.play() : video.pause()
  }

  // ─── Fullscreen ────────────────────────────────────

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      this.playerContainerTarget.requestFullscreen()
    }
  }

  // ─── Seeking ───────────────────────────────────────

  handleSeek(event) {
    const video = this.currentVideo
    if (!video) return
    if (this.seekHeld) return

    this.seekHeld = true
    this.seekDirection = event.key === "ArrowLeft" ? -1 : 1
    this.seekSkipped = false
    this.savedRate = video.playbackRate
    this.wasPlaying = !video.paused

    this.seekTimeout = setTimeout(() => {
      this.seekSkipped = true
      if (this.seekDirection === 1) {
        if (!this.wasPlaying) video.play()
        video.playbackRate = 4
      } else {
        video.pause()
        this.reverseInterval = setInterval(() => {
          video.currentTime = Math.max(0, video.currentTime - 0.15)
        }, 30)
      }
    }, 250)
  }

  stopFastSeek() {
    if (!this.seekHeld) return
    this.seekHeld = false
    clearTimeout(this.seekTimeout)

    const video = this.currentVideo
    if (!video) return

    if (!this.seekSkipped) {
      video.currentTime = Math.max(0, video.currentTime + (5 * this.seekDirection))
      if (this.wasPlaying) video.play()
    } else {
      clearInterval(this.reverseInterval)
      this.reverseInterval = null
      video.playbackRate = this.savedRate || 1
      this.savedRate = null
      if (this.wasPlaying) video.play()
      else video.pause()
    }
  }

  // ─── Highlight Marking ─────────────────────────────

  toggleMark() {
    if (this.marking) {
      this.markOut()
    } else {
      this.markIn()
    }
  }

  markIn() {
    const video = this.currentVideo
    if (!video) return

    this.markInTime = video.currentTime
    this.marking = true
    this.showMarkingBorder("#16a34a")
  }

  markOut() {
    const video = this.currentVideo
    if (!video || this.markInTime === null) return

    const endTime = video.currentTime
    if (endTime <= this.markInTime) return

    this.marking = false
    this.hideMarkingBorder()

    const prodVideo = this.currentProductionVideo
    if (!prodVideo) return

    this.apiPost("/api/highlights", {
      game_id: this.gameIdValue,
      video_id: prodVideo.id,
      start_time: this.markInTime,
      end_time: endTime
    }).then(data => {
      this.flashBorder("#16a34a")
      this.refreshGameData()
    })

    this.markInTime = null
  }

  cancelMark() {
    this.marking = false
    this.markInTime = null
    this.flashBorder("#dc2626")
  }

  // ─── Goal Marking ──────────────────────────────────

  addGoal(side) {
    const video = this.currentVideo
    if (!video) return

    const prodVideo = this.currentProductionVideo
    if (!prodVideo) return

    const color = side === "home" ? "#16a34a" : "#2563eb"
    this.flashBorder(color)

    this.apiPost("/api/goals", {
      video_id: prodVideo.id,
      timestamp: video.currentTime,
      side: side
    }).then(data => {
      // Update local data
      if (data.goal_events) {
        prodVideo.goal_events = data.goal_events
      }
      this.renderGoals()
      this.updateScoreDisplay()
    })
  }

  deleteGoal(videoId, index) {
    this.apiDelete(`/api/goals/${index}`, { video_id: videoId, index: index })
      .then(data => {
        const prodVideo = this.gameData.videos.find(v => v.id === videoId)
        if (prodVideo && data.goal_events) {
          prodVideo.goal_events = data.goal_events
        }
        this.renderGoals()
        this.updateScoreDisplay()
      })
  }

  // ─── Zoom / Pan ────────────────────────────────────

  toggleZoom() {
    this.zoomed = !this.zoomed
    const wrapper = this.videoWrapperTarget
    const indicator = this.zoomIndicatorTarget

    if (this.zoomed) {
      wrapper.style.overflow = "hidden"
      this.currentVideo.style.transform = "scale(1.5)"
      this.currentVideo.style.transformOrigin = `${this.panX * 100}% ${this.panY * 100}%`
      indicator.classList.remove("hidden")
      this.setupDrag()
    } else {
      this.currentVideo.style.transform = ""
      this.currentVideo.style.transformOrigin = ""
      wrapper.style.overflow = ""
      indicator.classList.add("hidden")
      this.teardownDrag()
    }
  }

  setupDrag() {
    this._onMouseDown = (e) => {
      if (e.target.closest('.plyr__controls')) return
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragStartPanX = this.panX
      this.dragStartPanY = this.panY
      e.preventDefault()
    }
    this._onMouseMove = (e) => {
      if (!this.dragging) return
      const rect = this.videoWrapperTarget.getBoundingClientRect()
      const dx = (e.clientX - this.dragStartX) / rect.width
      const dy = (e.clientY - this.dragStartY) / rect.height
      // Invert: drag right = pan left (lower origin %)
      this.panX = Math.max(0, Math.min(1, this.dragStartPanX - dx))
      this.panY = Math.max(0, Math.min(1, this.dragStartPanY - dy))
      this.currentVideo.style.transformOrigin = `${this.panX * 100}% ${this.panY * 100}%`
    }
    this._onMouseUp = () => { this.dragging = false }

    this.videoWrapperTarget.addEventListener("mousedown", this._onMouseDown)
    document.addEventListener("mousemove", this._onMouseMove)
    document.addEventListener("mouseup", this._onMouseUp)
  }

  teardownDrag() {
    if (this._onMouseDown) {
      this.videoWrapperTarget.removeEventListener("mousedown", this._onMouseDown)
      document.removeEventListener("mousemove", this._onMouseMove)
      document.removeEventListener("mouseup", this._onMouseUp)
    }
  }

  saveCropPosition() {
    if (!this.zoomed) return
    const video = this.currentVideo
    if (!video) return

    // Find the highlight clip that covers current playback time
    const prodVideo = this.currentProductionVideo
    if (!prodVideo) return

    const currentTime = video.currentTime
    const clip = this.gameData.highlights.find(h =>
      h.video_id === prodVideo.id &&
      currentTime >= h.start_time &&
      currentTime <= h.end_time
    )

    if (!clip) {
      this.flashBorder("#dc2626")
      return
    }

    this.apiPatch(`/api/highlights/${clip.id}`, {
      crop_x: this.panX,
      crop_y: this.panY
    }).then(() => {
      clip.crop_x = this.panX
      clip.crop_y = this.panY
      this.flashBorder("#16a34a")
      this.renderClips()
    })
  }

  // ─── Score Display ─────────────────────────────────

  updateScoreDisplay() {
    if (!this.gameData) return

    const game = this.gameData.game
    const team = this.gameData.team
    const homeText = game.home ? team.short_name : game.opponent_short_name
    const awayText = game.home ? game.opponent_short_name : team.short_name

    // Compute score up to current time across preceding videos + current
    const matchVideos = this.gameData.videos.filter(v => v.group_name === "Match")
    let homeScore = 0
    let awayScore = 0

    for (let i = 0; i < matchVideos.length; i++) {
      const v = matchVideos[i]
      const events = v.goal_events || []

      if (i < this.currentVideoIndex) {
        // All goals from previous videos count
        events.forEach(e => e.side === "home" ? homeScore++ : awayScore++)
      } else if (i === this.currentVideoIndex) {
        // Goals up to current time
        const currentTime = this.currentVideo?.currentTime || 0
        events.forEach(e => {
          const secs = this.timestampToSeconds(e.timestamp)
          if (secs <= currentTime) {
            e.side === "home" ? homeScore++ : awayScore++
          }
        })
        break
      }
    }

    if (this.hasScoreDisplayTarget) {
      this.scoreDisplayTarget.textContent = `${homeText} ${homeScore} : ${awayScore} ${awayText}`
    }
  }

  // ─── Sidebar Rendering ─────────────────────────────

  renderGoals() {
    if (!this.gameData || !this.hasGoalsListTarget) return

    const prodVideo = this.currentProductionVideo
    if (!prodVideo) return

    const game = this.gameData.game
    const team = this.gameData.team
    const events = prodVideo.goal_events || []

    this.goalsListTarget.innerHTML = events.map((event, index) => {
      const teamName = event.side === "home"
        ? (game.home ? team.short_name : game.opponent_short_name)
        : (game.home ? game.opponent_short_name : team.short_name)

      return `<li class="flex items-center justify-between py-1 px-2 rounded bg-gray-700/50">
        <span class="text-xs font-mono text-gray-300">${teamName} · ${event.timestamp}</span>
        <button onclick="this.closest('[data-controller=workspace]').__stimulus_controller.deleteGoal(${prodVideo.id}, ${index})"
                class="text-red-400 hover:text-red-300">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </li>`
    }).join("")
  }

  renderClips() {
    if (!this.gameData || !this.hasClipsListTarget) return

    const prodVideo = this.currentProductionVideo
    if (!prodVideo) return

    const clips = this.gameData.highlights.filter(h => h.video_id === prodVideo.id)

    this.clipsListTarget.innerHTML = clips.map(clip => {
      const cropLabel = (clip.crop_x !== 0.5 || clip.crop_y !== 0.5)
        ? `<span class="text-yellow-400 text-xs ml-1">⊞</span>` : ""

      return `<li class="flex items-center justify-between py-1 px-2 rounded bg-gray-700/50">
        <span class="text-xs font-mono text-gray-300">
          ${this.formatTime(clip.start_time)} - ${this.formatTime(clip.end_time)}
          <span class="text-gray-500">(${(clip.end_time - clip.start_time).toFixed(1)}s)</span>
          ${cropLabel}
        </span>
      </li>`
    }).join("")
  }

  // ─── API Helpers ───────────────────────────────────

  async apiPost(path, body) {
    const response = await fetch(`${this.apiBaseValue}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.apiTokenValue}`
      },
      body: JSON.stringify(body)
    })
    return response.json()
  }

  async apiPatch(path, body) {
    const response = await fetch(`${this.apiBaseValue}${path}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.apiTokenValue}`
      },
      body: JSON.stringify(body)
    })
    return response.json()
  }

  async apiDelete(path, body) {
    const response = await fetch(`${this.apiBaseValue}${path}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.apiTokenValue}`
      },
      body: JSON.stringify(body)
    })
    return response.json()
  }

  async refreshGameData() {
    const response = await fetch(`${this.apiBaseValue}/api/games/${this.gameIdValue}`, {
      headers: { "Authorization": `Bearer ${this.apiTokenValue}` }
    })
    this.gameData = await response.json()
    this.renderGoals()
    this.renderClips()
  }

  // ─── Visual Feedback ───────────────────────────────

  showMarkingBorder(color) {
    const wrapper = this.videoWrapperTarget
    let overlay = wrapper.querySelector("#marking-border")
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.id = "marking-border"
      overlay.style.cssText = "position:absolute;inset:0;pointer-events:none;z-index:100;"
      wrapper.style.position = "relative"
      wrapper.appendChild(overlay)
    }
    overlay.style.border = `3px solid ${color}`
    overlay.style.display = "block"
  }

  hideMarkingBorder() {
    const overlay = this.videoWrapperTarget.querySelector("#marking-border")
    if (overlay) overlay.style.display = "none"
  }

  flashBorder(color) {
    const wrapper = this.videoWrapperTarget
    let overlay = wrapper.querySelector("#marking-border")
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.id = "marking-border"
      overlay.style.cssText = "position:absolute;inset:0;pointer-events:none;z-index:100;transition:opacity 0.4s;"
      wrapper.style.position = "relative"
      wrapper.appendChild(overlay)
    }
    overlay.style.border = `3px solid ${color}`
    overlay.style.display = "block"
    overlay.style.opacity = "1"

    setTimeout(() => {
      overlay.style.opacity = "0"
      setTimeout(() => {
        overlay.style.display = "none"
        overlay.style.opacity = "1"
      }, 400)
    }, 200)
  }

  // ─── Utilities ─────────────────────────────────────

  timestampToSeconds(ts) {
    const [mins, secs] = ts.split(":").map(Number)
    return mins * 60 + secs
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }
}
